
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { LensCard } from "./LensCard";
import { LensEmptyState } from "./LensEmptyState";
import { LensSearchAndAdd } from "./LensSearchAndAdd";
import { LensLoadingSkeleton } from "./LensLoadingSkeleton";
import { LensInventoryError } from "./LensInventoryError";
import { useLensInventory } from "@/hooks/useLensInventory";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileLensInventory } from "./MobileLensInventory";
import { useTranslation } from "@/hooks/useTranslation";

export function LensInventoryContent() {
  const {
    lenses,
    loading,
    error,
    tablesExist,
    isAddDialogOpen,
    setIsAddDialogOpen,
    searchQuery,
    setSearchQuery,
    activeSort,
    sortDirection,
    handleSortChange,
    handleDirectionChange,
    form,
    handleAddLens,
    fetchLenses
  } = useLensInventory();
  
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const sortOptions = [
    { label: t("brand"), value: "brand" },
    { label: t("name"), value: "lens_name" },
    { label: t("status"), value: "status" },
    { label: t("last_updated"), value: "created_at" }
  ];

  const renderLensCard = (lens: any, index: number) => (
    <LensCard
      key={lens.id || index}
      lens={lens}
      onActionComplete={fetchLenses}
    />
  );

  if (!tablesExist) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Database Not Configured</AlertTitle>
        <AlertDescription>
          The lens inventory database tables have not been set up. Please contact your administrator to set up the required database tables.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <LensSearchAndAdd
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isAddDialogOpen={isAddDialogOpen}
        setIsAddDialogOpen={setIsAddDialogOpen}
        form={form}
        onSubmit={handleAddLens}
        activeSort={activeSort}
        sortDirection={sortDirection}
        handleSortChange={handleSortChange}
        handleDirectionChange={handleDirectionChange}
        sortOptions={sortOptions}
      />
      
      {loading ? (
        <LensLoadingSkeleton />
      ) : error ? (
        <LensInventoryError error={error} />
      ) : lenses.length === 0 ? (
        <LensEmptyState 
          searchQuery={searchQuery} 
          onAddLensClick={() => setIsAddDialogOpen(true)} 
        />
      ) : isMobile ? (
        <MobileLensInventory
          lenses={lenses}
          sortField={activeSort || "brand"}
          sortOrder={sortDirection === "asc" ? "asc" : "desc"}
          onSort={handleSortChange}
          onSortOrderChange={handleDirectionChange}
          onActionComplete={fetchLenses}
          renderItem={renderLensCard}
          sortOptions={sortOptions}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {lenses.map((lens, index) => renderLensCard(lens, index))}
        </div>
      )}
    </div>
  );
}
